// Board fields (06 §Boards and tasks: text, number, select, multi-select, seat, date, checkbox,
// URL, currency, formula, relation, rollup; audience `human | agent | both`). Adapted from Work OS
// `src/domain/fields.ts` (`defineField` :41, option normalisation :28-39, `validateValue`
// :313-352 — null always clears, an unchanged value is a no-op, the audience filters who reads a
// field :275-311). Work OS has no formula, currency or rollup: formula evaluation is the KPI
// engine's (`@companyos/domain-kpi` `evaluateFormula`, 06 "from `domain-kpi`"), rollups are new.
// Pure: existence of seats, tasks and boards a value names is the API's check.
import { evaluateFormula, type FormulaContext } from "@companyos/domain-kpi";

export const FIELD_TYPES = [
  "text",
  "number",
  "select",
  "multi_select",
  "seat",
  "date",
  "checkbox",
  "url",
  "currency",
  "formula",
  "relation",
  "rollup",
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export type Choice = { key: string; name: string; color?: string | undefined };
export type RollupAggregation = "count" | "sum" | "avg" | "min" | "max" | "percent_done";
export type FieldOptions = {
  choices?: Choice[] | undefined;
  currency?: string | undefined;
  precision?: number | undefined;
  formula?: string | undefined;
  relation_board_id?: string | undefined;
  rollup?:
    | { relation_field_id: string; field_id?: string | undefined; aggregation: RollupAggregation }
    | undefined;
};

export type FieldDefLike = {
  id: string;
  name: string;
  type: FieldType;
  options: FieldOptions;
  audience: "human" | "agent" | "both";
};

/** Computed from other values on read; never written (`PUT fields/{id}` refuses them). */
export const READ_ONLY_TYPES: ReadonlySet<FieldType> = new Set(["formula", "rollup"]);
/** Types whose value is a number to formulas and rollups. */
export const NUMERIC_TYPES: ReadonlySet<FieldType> = new Set([
  "number",
  "currency",
  "checkbox",
  "formula",
  "rollup",
]);

export const MAX_TEXT = 10_000;
export const MAX_URL = 2_048;
export const MAX_RELATED = 100;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/;
const CHOICE_KEY = /^[a-z0-9][a-z0-9_-]*$/;

// Options -----------------------------------------------------------------------------------------

/** What an options check needs to know about the board (sibling fields, by id). */
export type OptionsContext = { fields: readonly FieldDefLike[]; self?: string };

/** Problems with `options` for a field of `type` (empty when valid). */
export function optionsProblems(
  type: FieldType,
  options: FieldOptions,
  ctx: OptionsContext,
): string[] {
  const p: string[] = [];
  const has = (k: keyof FieldOptions) => options[k] !== undefined;
  const only = (allowed: (keyof FieldOptions)[]) => {
    for (const k of Object.keys(options) as (keyof FieldOptions)[])
      if (options[k] !== undefined && !allowed.includes(k))
        p.push(`options.${k} does not apply to a ${type} field`);
  };
  switch (type) {
    case "select":
    case "multi_select": {
      only(["choices"]);
      const choices = options.choices ?? [];
      if (choices.length === 0) p.push("options.choices: at least one choice");
      if (new Set(choices.map((c) => c.key)).size !== choices.length)
        p.push("options.choices: keys must be unique");
      for (const c of choices)
        if (!CHOICE_KEY.test(c.key)) p.push(`options.choices: key "${c.key}" is not a key`);
      break;
    }
    case "number":
      only(["precision"]);
      break;
    case "currency":
      only(["currency", "precision"]);
      if (has("currency") && !/^[A-Z]{3}$/.test(options.currency as string))
        p.push("options.currency: an ISO 4217 code");
      break;
    case "formula": {
      only(["formula", "precision"]);
      if (!options.formula?.trim()) p.push("options.formula: required");
      else p.push(...formulaProblems(options.formula, ctx));
      break;
    }
    case "relation":
      only(["relation_board_id"]);
      break;
    case "rollup": {
      only(["rollup", "precision"]);
      const r = options.rollup;
      if (!r) {
        p.push("options.rollup: required");
        break;
      }
      const rel = ctx.fields.find((f) => f.id === r.relation_field_id);
      if (!rel || rel.type !== "relation")
        p.push("options.rollup.relation_field_id: a relation field of this board");
      const needsField = r.aggregation !== "count" && r.aggregation !== "percent_done";
      if (needsField && !r.field_id)
        p.push(`options.rollup.field_id: required for ${r.aggregation}`);
      if (!needsField && r.field_id) p.push(`options.rollup.field_id: unused for ${r.aggregation}`);
      break;
    }
    default:
      only([]);
  }
  return p;
}

// Values ------------------------------------------------------------------------------------------

export type ValueResult = { ok: true; value: unknown } | { ok: false; message: string };

const bad = (message: string): ValueResult => ({ ok: false, message });

/** A timestamp or a date, normalised to an ISO-8601 UTC timestamp (dates at 00:00Z). */
export function normaliseInstant(v: unknown): string | null {
  if (typeof v !== "string") return null;
  if (DATE_ONLY.test(v)) {
    const d = new Date(`${v}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== v ? null : d.toISOString();
  }
  if (!DATE_TIME.test(v)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Whether `n` has at most `precision` decimal places (up to float noise). */
function fitsPrecision(n: number, precision: number): boolean {
  const scaled = n * 10 ** precision;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/**
 * Validates and normalises a value written to `def` (`null` clears). Returns the jsonb to store.
 * Seats, related tasks and boards are only shape-checked here.
 */
export function validateValue(def: FieldDefLike, value: unknown): ValueResult {
  if (value === null) return { ok: true, value: null };
  if (READ_ONLY_TYPES.has(def.type))
    return bad(`${def.type} fields are computed; they take no value`);
  const o = def.options;
  switch (def.type) {
    case "text":
      if (typeof value !== "string") return bad("a string");
      if (value.length > MAX_TEXT) return bad(`at most ${MAX_TEXT} characters`);
      return { ok: true, value };
    case "number":
      if (typeof value !== "number" || !Number.isFinite(value)) return bad("a finite number");
      if (o.precision !== undefined && !fitsPrecision(value, o.precision))
        return bad(`at most ${o.precision} decimal places`);
      return { ok: true, value };
    case "select": {
      if (typeof value !== "string") return bad("a choice key");
      if (!(o.choices ?? []).some((c) => c.key === value)) return bad(`no choice "${value}"`);
      return { ok: true, value };
    }
    case "multi_select": {
      if (!Array.isArray(value) || value.some((v) => typeof v !== "string"))
        return bad("an array of choice keys");
      const keys = new Set((o.choices ?? []).map((c) => c.key));
      const unknown = value.filter((v) => !keys.has(v as string));
      if (unknown.length > 0) return bad(`no choice ${unknown.map((u) => `"${u}"`).join(", ")}`);
      // Stored in the board's choice order, without duplicates.
      const set = new Set(value as string[]);
      return { ok: true, value: (o.choices ?? []).map((c) => c.key).filter((k) => set.has(k)) };
    }
    case "seat":
      if (typeof value !== "string" || !UUID.test(value)) return bad("a seat id");
      return { ok: true, value: value.toLowerCase() };
    case "date": {
      const at = normaliseInstant(value);
      return at === null ? bad("an ISO-8601 date or timestamp") : { ok: true, value: at };
    }
    case "checkbox":
      if (typeof value !== "boolean") return bad("true or false");
      return { ok: true, value };
    case "url": {
      if (typeof value !== "string" || value.length > MAX_URL) return bad("a URL");
      try {
        const u = new URL(value);
        if (u.protocol !== "http:" && u.protocol !== "https:") return bad("an http(s) URL");
      } catch {
        return bad("a URL");
      }
      return { ok: true, value };
    }
    case "currency": {
      const v = value as { amount_micros?: unknown; currency?: unknown };
      if (typeof value !== "object" || Array.isArray(value))
        return bad("{amount_micros, currency}");
      const keys = Object.keys(value as object);
      if (keys.some((k) => k !== "amount_micros" && k !== "currency"))
        return bad("{amount_micros, currency} only");
      if (typeof v.amount_micros !== "number" || !Number.isSafeInteger(v.amount_micros))
        return bad("amount_micros: an integer");
      const currency = v.currency ?? o.currency;
      if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency))
        return bad("currency: an ISO 4217 code");
      if (o.currency !== undefined && currency !== o.currency)
        return bad(`currency: this field is in ${o.currency}`);
      return { ok: true, value: { amount_micros: v.amount_micros, currency } };
    }
    case "relation": {
      if (!Array.isArray(value) || value.some((v) => typeof v !== "string" || !UUID.test(v)))
        return bad("an array of task ids");
      const ids = [...new Set((value as string[]).map((v) => v.toLowerCase()))];
      if (ids.length > MAX_RELATED) return bad(`at most ${MAX_RELATED} related tasks`);
      return { ok: true, value: ids };
    }
    default:
      return bad("unknown field type");
  }
}

/** Value equality for the "unchanged value is a no-op" rule (jsonb semantics). */
export function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

function canonical(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === "object")
    return Object.fromEntries(
      Object.keys(v as object)
        .sort()
        .map((k) => [k, canonical((v as Record<string, unknown>)[k])]),
    );
  return v ?? null;
}

/** Whether a principal kind reads a field (05 `field_def.audience`). */
export function audienceSees(
  audience: FieldDefLike["audience"],
  principal: "human" | "agent",
): boolean {
  return audience === "both" || audience === principal;
}

// Formulas ----------------------------------------------------------------------------------------

/**
 * A board formula is the KPI formula DSL where `field('Name')` (or `metric('Name')`) names a
 * numeric field of the board, by name or id, plus the task's built-in numbers:
 * `priority`, `estimate_minutes`, `checklist_done`, `checklist_total`.
 */
export const FORMULA_VARIABLES = [
  "priority",
  "estimate_minutes",
  "checklist_done",
  "checklist_total",
] as const;
export type FormulaVariables = Record<(typeof FORMULA_VARIABLES)[number], number | null>;

/** `field(` → `metric(` outside string literals (the KPI tokenizer knows `metric`). */
export function toKpiFormula(src: string): string {
  let out = "";
  let quote: string | null = null;
  for (let i = 0; i < src.length; i++) {
    const c = src[i] as string;
    if (quote) {
      out += c;
      if (c === quote) quote = null;
      continue;
    }
    if (c === "'" || c === '"') {
      quote = c;
      out += c;
      continue;
    }
    const prev = i > 0 ? (src[i - 1] as string) : "";
    if (src.startsWith("field", i) && !/[A-Za-z0-9_.]/.test(prev)) {
      const rest = src.slice(i + 5);
      if (/^\s*\(/.test(rest)) {
        out += "metric";
        i += 4;
        continue;
      }
    }
    out += c;
  }
  return out;
}

/** Field names or ids a formula references (`field('x')` / `metric('x')`). */
export function formulaReferences(src: string): string[] {
  const refs: string[] = [];
  const re = /\b(?:field|metric)\s*\(\s*(['"])(.*?)\1\s*\)/g;
  for (const m of src.matchAll(re)) refs.push(m[2] as string);
  return [...new Set(refs)];
}

const findField = (fields: readonly FieldDefLike[], ref: string) =>
  fields.find((f) => f.id === ref) ??
  fields.find((f) => f.name.toLowerCase() === ref.toLowerCase());

/** Syntax, references (numeric fields of the board) and cycles among formula fields. */
export function formulaProblems(src: string, ctx: OptionsContext): string[] {
  const problems: string[] = [];
  try {
    const zero: FormulaContext = {
      resolveSelf: () => 0,
      resolveChildren: () => [],
      resolveSubtree: () => [],
      resolveVar: (n) => ((FORMULA_VARIABLES as readonly string[]).includes(n) ? 0 : undefined),
    };
    evaluateFormula(toKpiFormula(src), zero);
  } catch (e) {
    return [`options.formula: ${(e as Error).message}`];
  }
  for (const ref of formulaReferences(src)) {
    const f = findField(ctx.fields, ref);
    if (!f) problems.push(`options.formula: no field "${ref}" on this board`);
    else if (!NUMERIC_TYPES.has(f.type))
      problems.push(`options.formula: "${ref}" is a ${f.type} field, not a number`);
    else if (f.id === ctx.self) problems.push("options.formula: a formula cannot use itself");
  }
  if (problems.length === 0 && ctx.self && formulaCycle(ctx.fields, ctx.self, src))
    problems.push("options.formula: formulas would reference each other in a cycle");
  return problems;
}

function formulaCycle(fields: readonly FieldDefLike[], self: string, src: string): boolean {
  const visit = (refs: string[], path: Set<string>): boolean => {
    for (const ref of refs) {
      const f = findField(fields, ref);
      if (!f || f.type !== "formula") continue;
      if (f.id === self || path.has(f.id)) return true;
      if (visit(formulaReferences(f.options.formula ?? ""), new Set([...path, f.id]))) return true;
    }
    return false;
  };
  return visit(formulaReferences(src), new Set([self]));
}

/** A field value as a number (formula and rollup inputs); null when it has none. */
export function numericValue(def: FieldDefLike, value: unknown): number | null {
  if (value === null || value === undefined) return null;
  switch (def.type) {
    case "number":
    case "formula":
    case "rollup":
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    case "currency": {
      const m = (value as { amount_micros?: unknown }).amount_micros;
      return typeof m === "number" ? m / 1_000_000 : null;
    }
    case "checkbox":
      return value === true ? 1 : 0;
    default:
      return null;
  }
}

function roundTo(n: number | null, precision: number | undefined): number | null {
  if (n === null || !Number.isFinite(n)) return null;
  if (precision === undefined) return n;
  const f = 10 ** precision;
  return Math.round(n * f) / f;
}

/**
 * Every computed value of one task: formulas (in dependency order) over `values` (stored values
 * and rollups, by field id) and the task's built-ins. A formula that fails to evaluate is null.
 */
export function computeFormulas(
  fields: readonly FieldDefLike[],
  values: Readonly<Record<string, unknown>>,
  vars: FormulaVariables,
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const evaluating = new Set<string>();
  const numberOf = (f: FieldDefLike): number | null => {
    if (f.type !== "formula") return numericValue(f, values[f.id]);
    if (f.id in out) return out[f.id] ?? null;
    if (evaluating.has(f.id)) return null; // a cycle slipped in: no value, never a loop
    evaluating.add(f.id);
    let result: number | null = null;
    try {
      result = evaluateFormula(toKpiFormula(f.options.formula ?? ""), {
        resolveSelf: (ref) => {
          const g = findField(fields, ref);
          return g ? numberOf(g) : null;
        },
        resolveChildren: () => [],
        resolveSubtree: () => [],
        resolveVar: (n) =>
          (FORMULA_VARIABLES as readonly string[]).includes(n)
            ? vars[n as keyof FormulaVariables]
            : undefined,
      });
    } catch {
      result = null;
    }
    evaluating.delete(f.id);
    out[f.id] = roundTo(result, f.options.precision);
    return out[f.id] ?? null;
  };
  for (const f of fields) if (f.type === "formula") numberOf(f);
  return out;
}

// Rollups -----------------------------------------------------------------------------------------

/** A related task as a rollup sees it: done or not, and the rolled-up field's value. */
export type RelatedTask = { done: boolean; value: unknown };

/** A rollup over a relation's tasks; null when there is nothing to aggregate. */
export function rollupValue(
  aggregation: RollupAggregation,
  related: readonly RelatedTask[],
  field: FieldDefLike | undefined,
  precision?: number,
): number | null {
  if (aggregation === "count") return related.length;
  if (aggregation === "percent_done")
    return related.length === 0
      ? null
      : roundTo((related.filter((r) => r.done).length / related.length) * 100, precision ?? 2);
  if (!field) return null;
  const nums = related.map((r) => numericValue(field, r.value)).filter((n) => n !== null);
  if (nums.length === 0) return null;
  const v =
    aggregation === "sum"
      ? nums.reduce((s, n) => s + n, 0)
      : aggregation === "avg"
        ? nums.reduce((s, n) => s + n, 0) / nums.length
        : aggregation === "min"
          ? Math.min(...nums)
          : Math.max(...nums);
  return roundTo(v, precision);
}
